<template>
  <div class="reqWrap">
    <div class="u-containar-md">
      <Alert
        text="申込み内容をご確認の上、「申込む」ボタンを押してください。"
      />

      <div class="reqForm">
        <section>
          <HeadingLv3
            :text="`${hospitalInfo.length}件の申込み先病院`"
            v-if="hospitalInfo.length > 1"
          />
          <HeadingLv3 text="申込み先病院" v-else />
          <ApplyHotelAbstract :hospitalList="hospitalInfo" />
        </section>
        <section>
          <HeadingLv3 text="希望する内容" />
          <PreviewPairData
            v-for="(item, key) in data.contents"
            :key="key"
            :label="item.label"
            :content="item.content"
          />
        </section>

        <section>
          <HeadingLv3 text="登録情報" />
          <PreviewPairData
            :label="data.user.name.label"
            :content="data.user.name.content"
          />
          <PreviewPairData
            :label="data.user.name_kana.label"
            :content="data.user.name_kana.content"
          />
          <PreviewPairData
            :label="data.user.gender.label"
            :content="data.user.gender.content"
          />
          <PreviewPairData
            :label="data.user.birthday.label"
            :content="data.user.birthday.content"
          />
          <PreviewPairData
            :label="data.user.email.label"
            :content="data.user.email.content"
          />

          <PreviewPairData
            v-if="data.user.address"
            :label="data.user.address.label"
            :content="data.user.address.content"
          />
          <PreviewPairData
            v-if="!data.user.address"
            label="住所"
            :content="
              getAddressPreviewHtml(
                notEnterdData.zip,
                prefectures[notEnterdData.prefecture - 1].option_name,
                notEnterdData.city,
                notEnterdData.town
              )
            "
          />

          <PreviewPairData
            v-if="data.user.tel.content"
            :label="data.user.tel.label"
            :content="data.user.tel.content"
          />

          <PreviewPairData
            v-if="!data.user.tel.content"
            label="電話番号"
            :content="notEnterdData.tel"
          />

          <PreviewPairData
            v-if="data.user.address_vacation"
            :label="data.user.address_vacation.label"
            :content="data.user.address_vacation.content"
          />

          <PreviewPairData
            v-if="!data.user.address_vacation && notEnterdData.vacation_flg"
            label="休暇中住所"
            content="上記の住所と同じ"
          />

          <PreviewPairData
            v-if="!data.user.address_vacation && !notEnterdData.vacation_flg"
            label="休暇中住所"
            :content="
              getAddressPreviewHtml(
                notEnterdData.zip_vacation,
                prefectures[notEnterdData.prefecture_vacation - 1].option_name,
                notEnterdData.city_vacation,
                notEnterdData.town_vacation
              )
            "
          />

          <PreviewPairData
            :label="data.user.school.label"
            :content="data.user.school.content"
          />
          <PreviewPairData
            v-if="data.user.graduate_year"
            :label="data.user.graduate_year.label"
            :content="data.user.graduate_year.content"
          />
          <PreviewPairData
            v-if="data.user.work_place"
            :label="data.user.work_place.label"
            :content="data.user.work_place.content"
          />
          <PreviewPairData
            v-if="data.user.licensing_year"
            :label="data.user.licensing_year.label"
            :content="data.user.licensing_year.content"
          />
        </section>
      </div>
    </div>

    <GoalButtons type="contents">
      <Button label="戻る" :handleClick="() => $router.go(-1)" />
      <Button label="上記の内容で申し込む" primary :handleClick="submitForm" />
    </GoalButtons>
  </div>
</template>

<script lang="ts">
import { SetupContext } from 'vue'
import Button from '@/components/elements/Button.vue'
import HeadingLv3 from '@/components/elements/HeadingLv3.vue'
import PreviewPairData from '@/components/block/PreviewPairData.vue'
import Alert from '@/components/elements/Alert.vue'
import GoalButtons from '@/components/block/GoalButtons.vue'
import ApplyHotelAbstract from '@/components/block/ApplyHotelAbstract.vue'
import { axiosSetting } from '@/utils/axiosSetting'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { useStore } from 'vuex'
import { getAddressPreviewHtml } from '@/utils/commonMethods'
import prefectures from '../../../public/json/prefectures.json'

export default {
  name: 'DocumentRequestConfirm',

  components: {
    Button,
    HeadingLv3,
    PreviewPairData,
    Alert,
    GoalButtons,
    ApplyHotelAbstract
  },

  props: {
    state: {
      type: Object,
      require: true
    },
    hospitalInfo: {
      type: Array,
      default: () => []
    },
    data: {
      type: Object,
      default: () => ({})
    },
    notEnterdData: {
      type: Object,
      default: () => ({})
    }
  },

  setup(props: any, context: SetupContext) {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    // 入力ページ経由じゃない場合は弾く
    if (!props.state.isLandingInput) {
      router.replace('/500')
    }

    // ページの更新をしようとした時に確認ダイアログを表示
    window.onbeforeunload = function(e: any) {
      e.returnValue =
        'このページを再読み込みしますか？行った変更が保存されない可能性があります。'
    }

    // ページ遷移時に確認ダイアログ解除
    onBeforeRouteLeave((to, from) => {
      window.onbeforeunload = null
    })

    // 申し込みフローの更新
    context.emit('update-flow', 2)

    const submitForm = () => {
      store.commit('showLoading')
      const paramsIds: any = route.params.id
      const ids = paramsIds.split('_')
      const params: any = {
        ids: ids.map((id: string) => {
          return { hospital_id: id }
        }),
        note: props.data.contents.note.content
      }

      if (!props.data.user.address) {
        params.zip = props.notEnterdData.zip
        params.prefecture = props.notEnterdData.prefecture
        params.city = props.notEnterdData.city
        params.town = props.notEnterdData.town
      }

      if (!props.data.user.tel.content) {
        params.tel = props.notEnterdData.tel
      }

      if (!props.data.user.address_vacation) {
        if (props.notEnterdData.vacation_flg) {
          params.vacation_flg = true
        } else {
          params.zip_vacation = props.notEnterdData.zip_vacation
          params.prefecture_vacation = props.notEnterdData.prefecture_vacation
          params.city_vacation = props.notEnterdData.city_vacation
          params.town_vacation = props.notEnterdData.town_vacation
          params.vacation_flg = false
        }
      }

      const ajax = axiosSetting('app', 'action')
      ajax
        .post('/api/v1/request_entry', params)
        .then((res) => {
          router.push({
            path: `/document_request/${route.params.id}/thanks`
          })
          store.dispatch('getUserInfo')
        })
        .catch((err: any) => {
          if (err.response.status === 422) {
            context.emit('catch-error', err.response.data.err_msg)
            router.go(-1)
            return
          }
          store.commit('closeLoading')
          store.commit('showSystemError', 'modal')
        })
        .then(() => {
          store.commit('closeLoading')
        })
    }

    return {
      submitForm,
      getAddressPreviewHtml,
      prefectures
    }
  }
}
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.reqWrap {
  margin-bottom: -30px;
}

.reqForm {
  overflow: hidden;
  margin-bottom: -25px;

  section {
    margin-bottom: 40px;

    @include mixins.media() {
      margin-bottom: 60px;
    }
  }

  ::v-deep .headingLv3 {
    margin-bottom: 20px;
  }

  ::v-deep .inputDataPreview + .formField {
    margin-top: 28px;
  }

  ::v-deep .formGroup + .inputDataPreview,
  ::v-deep .formField + .inputDataPreview {
    margin-top: 28px;
    border-top: 1px solid #e4e4e4;
    padding-top: 20px;
  }
}

.reqForm_hospital {
  & + & {
    margin-top: 28px;
  }

  h4 {
    font-weight: 500;
    margin-bottom: 10px;
  }
}
</style>
